import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import { SFSOptions } from '../../data-table/abstractdata.service';
import { SecondaryUserFilter, User } from '../../models/user';
import { AuthService } from '../auth.service';
import { Pageable } from '../../models/pageable';
import { WorkflowDataService } from '../../data-table/workflow-data.service';
import { serializeUserRolesForRequest } from '../../utils/authorization.util';

@Injectable({ providedIn: 'root' })
export class SecondaryUserService extends WorkflowDataService<User> {
  protected override get apiEndpoint(): string {
    return '/user';
  }

  public secondaryUserFilter = SecondaryUserFilter.ONLY_SECONDARY_USERS;

  private authService = inject(AuthService);

  private getOrgApiEndpoint(): Observable<string> {
    return this.authService.getCurrentUser().pipe(
      map((user) => `/organization/${user!.currentOrganization!.id}/user`),
    );
  }

  override list(options: Partial<SFSOptions> = {}): Observable<Pageable<User>> {
    let params = this.getParams(options);
    params = params.append('secondaryUserFilter', this.secondaryUserFilter);
    return this.getOrgApiEndpoint().pipe(
      switchMap((endpoint) => this.http.get<Pageable<User>>(this.baseUrl + endpoint, { params })),
    );
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.baseUrl + this.apiEndpoint}`);
  }

  override create(user: User): Observable<User> {
    const createUser = {
      firstName: user.given_name,
      lastName: user.family_name,
      email: user.email,
      roles: serializeUserRolesForRequest(user.roles ?? [user.role]),
    };
    if (user.organizationId) {
      return this.http.post<User>(`${this.baseUrl}/organization/${user.organizationId}/user`, createUser);
    } else {
      return this.getOrgApiEndpoint().pipe(
        switchMap((endpoint) => this.http.post<User>(`${this.baseUrl + endpoint}`, createUser)),
      );
    }
  }

  override update(user: User): Observable<User> {
    const editUser = {
      firstName: user.given_name,
      lastName: user.family_name,
      email: user.email,
      organizationId: user.organization?.id,
      ...(user.roles !== undefined
        ? { roles: serializeUserRolesForRequest(user.roles) }
        : {}),
    };
    return this.http.put<User>(`${this.baseUrl + this.apiEndpoint}/${user.id}`, editUser);
  }

  updateProfile(user: User): Observable<User> {
    const profile = {
      firstName: user.given_name,
      lastName: user.family_name,
    };
    return this.http.post<User>(`${this.baseUrl + this.apiEndpoint}/update-profile`, profile);
  }

  lock(user: User): Observable<void> {
    return this.http.post<void>(`${this.baseUrl + this.apiEndpoint}/${user.id}/lock`, {});
  }

  unlock(user: User): Observable<void> {
    return this.http.post<void>(`${this.baseUrl + this.apiEndpoint}/${user.id}/unlock`, {});
  }

  resetPassword(user: User): Observable<void> {
    return this.http.post<void>(`${this.baseUrl + this.apiEndpoint}/${user.id}/reset-password`, {});
  }

  saveSecondaryOrgs(userId: string, orgIds: string[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl + this.apiEndpoint}/${userId}/secondary-orgs`, orgIds ?? []);
  }

  removeSecondaryOrg(userId: string): Observable<void> {
    return this.authService.getCurrentUser().pipe(
      switchMap((user) => this.http.delete<void>(`${this.baseUrl + this.apiEndpoint}/${userId}/secondary-orgs/${user.currentOrganization?.id}`)),
    );
  }
}
